/**
 * State-specific terms for states that previously had no section (the
 * contract printed nothing for a Texas property, for example). Same shape
 * and tone as the hand-written components in components/Contract/State:
 * required disclosures, closing customs, and the key state forms.
 *
 * Drafted from public sources (state statutes / regulator guidance, NAR
 * transfer-tax summary) — should be reviewed by counsel for each state
 * before relying on it.
 */
export interface StateTermItem {
  text: string;
  link?: { label: string; url: string };
}

const LEAD_PAINT: StateTermItem = {
  text: '– Required by federal law for residences built before 1978; Buyer receives the EPA pamphlet and a 10-day opportunity to test for lead-based paint unless waived.',
  link: {
    label: 'Lead-Based Paint Disclosure',
    url: 'https://www.epa.gov/lead/real-estate-disclosure',
  },
};

export const STATE_TERMS: Record<string, StateTermItem[]> = {
  Texas: [
    {
      text: '– Required for resale of most residential property (Tex. Prop. Code § 5.008); must be delivered to Buyer on or before the effective date of this Agreement. If not delivered, Buyer may terminate within 7 days after receiving it.',
      link: {
        label: "Seller's Disclosure Notice",
        url: 'https://www.trec.texas.gov/forms/sellers-disclosure-notice',
      },
    },
    {
      text: 'Special district notices: if the Property is in a municipal utility district (MUD), public improvement district (PID) or similar taxing district, Seller must provide the statutory notice (Tex. Water Code § 49.452; Tex. Prop. Code § 5.014).',
    },
    {
      text: 'Property owners association: if the Property is subject to a mandatory owners association, Seller will provide the subdivision information and resale certificate required by Tex. Prop. Code § 207.003.',
    },
    {
      text: 'Texas has no state real estate transfer tax. Closings are customarily handled by a title company acting as escrow agent.',
    },
    LEAD_PAINT,
  ],
  'New York': [
    {
      text: 'Property Condition Disclosure Statement: Seller must complete, sign and deliver the statement before Buyer signs a binding contract (N.Y. Real Prop. Law § 462). Since March 20, 2024 the former $500 credit in lieu of disclosure is no longer available.',
    },
    {
      text: 'Flood disclosure: the statement must disclose whether the Property is in a FEMA 100-year or 500-year floodplain, flood insurance requirements, and any prior flood damage claims or FEMA/SBA assistance.',
    },
    {
      text: 'Transfer taxes: the New York State transfer tax is customarily paid by Seller; the additional "mansion tax" on residential purchases of $1,000,000 or more is paid by Buyer. New York City and some localities impose additional transfer taxes.',
    },
    {
      text: 'Attorney review: closings are customarily handled by attorneys for each party.',
    },
    LEAD_PAINT,
  ],
  'North Carolina': [
    {
      text: 'Residential Property and Owners’ Association Disclosure Statement and Mineral and Oil and Gas Rights Mandatory Disclosure Statement must be delivered to Buyer before an offer is made (N.C. Gen. Stat. Chapter 47E). If not delivered, Buyer may cancel within 3 calendar days after receipt or contract, whichever is earlier, and before closing or occupancy.',
    },
    {
      text: 'Due diligence: any due diligence fee and period agreed by the parties control Buyer’s right to terminate for any reason during that period.',
    },
    {
      text: 'Closing attorney: a North Carolina licensed attorney must conduct the closing.',
    },
    {
      text: 'Excise (transfer) tax of $1 per $500 of consideration is customarily paid by Seller.',
    },
    LEAD_PAINT,
  ],
  'North Dakota': [
    {
      text: 'No state-mandated seller disclosure form; Seller must answer Buyer’s questions truthfully and may not conceal known material defects.',
    },
    { text: 'North Dakota has no state real estate transfer tax.' },
    LEAD_PAINT,
  ],
  Oklahoma: [
    {
      text: 'Residential Property Condition Disclosure Statement or Disclaimer Statement must be delivered to Buyer before an offer is accepted (Okla. Stat. tit. 60, §§ 831–839). Seller must disclose defects actually known even when using the disclaimer.',
    },
    {
      text: 'Documentary stamp tax of $0.75 per $500 of consideration is customarily paid by Seller.',
    },
    LEAD_PAINT,
  ],
  Oregon: [
    {
      text: 'Seller’s Property Disclosure Statement must be delivered to Buyer (ORS 105.464). Buyer may revoke the offer by written notice within 5 business days after receiving the statement, unless Buyer waived that right.',
    },
    {
      text: 'Oregon has no state real estate transfer tax; Washington County imposes a local transfer tax.',
    },
    LEAD_PAINT,
  ],
  'Rhode Island': [
    {
      text: 'Real Estate Sales Disclosure form must be delivered to Buyer before the purchase agreement is signed (R.I. Gen. Laws § 5-20.8-2).',
    },
    {
      text: 'Buyer has the statutory 10-day opportunity for a lead inspection for pre-1978 housing, and radon and private well/septic disclosures apply where relevant.',
    },
    {
      text: 'Transfer (conveyance) tax is customarily paid by Seller.',
    },
    LEAD_PAINT,
  ],
  'South Carolina': [
    {
      text: 'Residential Property Condition Disclosure Statement must be delivered to Buyer before the contract is signed (S.C. Code § 27-50-10 et seq.).',
    },
    {
      text: 'Closing attorney: a South Carolina licensed attorney must supervise the closing.',
    },
    {
      text: 'Deed recording fee (state and county) is customarily paid by Seller.',
    },
    LEAD_PAINT,
  ],
  'South Dakota': [
    {
      text: 'Seller’s Property Condition Disclosure Statement must be delivered to Buyer before an offer is accepted (SDCL 43-4-38 et seq.).',
    },
    {
      text: 'Transfer fee of $0.50 per $500 of value is customarily paid by Seller.',
    },
    LEAD_PAINT,
  ],
  Tennessee: [
    {
      text: 'Tennessee Residential Property Condition Disclosure, Disclaimer or Exemption statement must be provided before Buyer makes an offer (Tenn. Code Ann. § 66-5-201 et seq.).',
    },
    {
      text: 'Recordation (transfer) tax of $0.37 per $100 of consideration is customarily paid by Buyer.',
    },
    LEAD_PAINT,
  ],
  Utah: [
    {
      text: 'No statutory seller disclosure form; Seller may not misrepresent or conceal known material defects. The Seller Property Condition Disclosure form is customarily used.',
    },
    {
      text: 'Seller must disclose any known contamination from methamphetamine use, storage or manufacture (Utah Code § 57-27-201).',
    },
    { text: 'Utah has no state real estate transfer tax.' },
    LEAD_PAINT,
  ],
  Vermont: [
    {
      text: 'No statewide mandatory seller disclosure form; Seller may not misrepresent or conceal known material defects. Seller must disclose known private road and certain water, septic and flood information where applicable.',
    },
    {
      text: 'Property transfer tax is customarily paid by Buyer; a property transfer tax return is filed at closing.',
    },
    LEAD_PAINT,
  ],
  Washington: [
    {
      text: 'Seller Disclosure Statement (RCW 64.06) must be delivered to Buyer; Buyer may rescind within 3 business days after receiving it.',
    },
    {
      text: 'Real estate excise tax (graduated state rate plus any local rate) is paid by Seller.',
    },
    LEAD_PAINT,
  ],
  'West Virginia': [
    {
      text: 'No state-mandated seller disclosure form; Seller may not conceal known material defects that a reasonable inspection would not reveal.',
    },
    {
      text: 'State and county excise (transfer) tax is customarily paid by Seller.',
    },
    { text: 'Closings are customarily conducted by an attorney.' },
    LEAD_PAINT,
  ],
  Wisconsin: [
    {
      text: 'Real Estate Condition Report must be delivered to Buyer within 10 days after acceptance; Buyer may rescind within 2 business days after receiving it (Wis. Stat. ch. 709).',
    },
    {
      text: 'Real estate transfer fee of $3 per $1,000 of value is customarily paid by Seller.',
    },
    LEAD_PAINT,
  ],
  Wyoming: [
    {
      text: 'No state-mandated seller disclosure form; Seller remains liable for fraudulent concealment or misrepresentation of known defects.',
    },
    { text: 'Wyoming has no state real estate transfer tax.' },
    LEAD_PAINT,
  ],
};
